import React, { useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import WarningIcon from "@material-ui/icons/Warning";
import EditTaskDialog from "./EditTaskDialog";
import TimerTwoToneIcon from "@material-ui/icons/TimerTwoTone";
import { taskStatuses } from "utils/static/workflowsStaticData";

const useStyles = makeStyles((theme) => ({
    task: {
        minHeight: 140,
        marginBottom: 10,
        backgroundColor: "white",
        borderRadius: 5,
        padding: 10,
        cursor: "pointer",
        borderLeft: "5px #808080 solid",
        borderBottom: "2px #3f51b521 solid",
        maxHeight: 150,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        paddingBottom: 5,
        "&:hover": {
            borderTop: () => "1px #3f51b521 solid",
            borderRight: () => "1px #3f51b521 solid",
            borderBottom: () => "1px #3f51b521 solid",
        },
    },
    taskName: {
        wordBreak: "break-word",
        maxHeight: 70,
        minHeight: 70,
        overflowY: "hidden",
        fontSize: "smaller",
        position: "relative",
    },
    textShadow: {
        position: "absolute",
        bottom: 0,
        height: 5,
        backgroundColor: "#fffffffc",
        width: "100%",
        "-webkit-box-shadow": "0px -7px 31px 26px #ffffff",
        "box-shadow": "0px-7px 31px 26px #ffffff",
    },
    taskModuleInfo: {
        display: "flex",
        justifyContent: "space-between",
        marginTop: 10,
        "&:hover": {
            color: () => "dodgerblue",
            textDecoration: () => "underline",
        },
    },
    taskModuleInfoModule: {
        textTransform: "capitalize",
        fontSize: "smaller",
        alignSelf: "flex-end",
    },
    taskModuleInfoFileNo: { textTransform: "uppercase", fontSize: "small", alignSelf: "flex-end" },
    taskOtherInfo: {
        display: "flex",
        justifyContent: "space-between",
        marginTop: 10,
    },
    taskOtherInfoId: {
        fontSize: "smaller",
        color: "silver",
    },
    taskOtherInfoDueDate: {
        fontSize: "x-small",
        color: "silver",
    },
    alertIcon_aboutToExpire: {
        color: "#ffcc00",
        width: 20,
        height: 20,
    },
    alertIcon_expired: {
        color: "#ff3333",
        width: 20,
        height: 20,
    },
    waitingForPreviousTask: {
        color: "#3f51b5",
        width: 20,
        height: 20,
        marginLeft: 10,
    },
}));

function Task({ task, resources, reloadTasks }) {
    const classes = useStyles();

    const [formDialogProps, setFormDialogProps] = useState({
        open: false,
        maxWidth: "lg",
        fullWidth: true,
        title: task.name,
        formId: `in-dialog-form-task_${task.id}`,
        onClose: () => {
            setFormDialogProps((prev) => ({ ...prev, open: false }));
        },
    });

    if (!task) {
        return <div>No task information!</div>;
    }

    function onClickTaskHandler() {
        setFormDialogProps((prev) => ({ ...prev, open: true }));
    }

    const {
        id,
        name,
        moduleInfo: { module, fileCaseId, fileNo },
        dueAlert,
        dueDateString,
        finished,
        waitForPreviousTask,
        previousTaskId,
        status
    } = task;

    const statusFontColor = taskStatuses.find(x => x.value === status)?.style?.color;

    return (
        <>
            <div className={classes.task} style={{ borderLeftColor: statusFontColor }}>
                <div className={classes.taskName} onClick={onClickTaskHandler}>
                    {name}
                    <div className={classes.textShadow} />
                </div>
                <Link
                    className={classes.taskModuleInfo}
                    to={{
                        pathname: `/${module}`,
                        search: `?fileCaseId=${fileCaseId}`,
                    }}
                    target="_blank"
                >
                    <div className={classes.taskModuleInfoModule}>{module}</div>
                    <div className={classes.taskModuleInfoFileNo}>{fileNo}</div>{" "}
                </Link>
                <div className={classes.taskOtherInfo}>
                    <div className={classes.taskOtherInfoId} onClick={onClickTaskHandler}>
                        #{id}
                    </div>
                    {waitForPreviousTask && previousTaskId && !finished && (
                        <div title="Waiting for the previous task to be closed!">
                            <TimerTwoToneIcon className={classes.waitingForPreviousTask} />
                        </div>
                    )}
                    {dueDateString && (
                        <div className={classes.taskOtherInfoDueDate} onClick={onClickTaskHandler}>
                            Exp. {dueDateString}
                        </div>
                    )}
                    {dueAlert && !finished && (
                        <div className={classes.taskOtherInfoDueAlert} title={dueAlert.message} onClick={onClickTaskHandler}>
                            <WarningIcon className={classes[`alertIcon_${dueAlert.dueStatus}`]} />
                        </div>
                    )}
                </div>
            </div>
            <EditTaskDialog task={task} resources={resources} onSuccessCallback={reloadTasks} {...formDialogProps} />
        </>
    );
}

Task.propTypes = {
    task: PropTypes.shape({
        allowAssigneeReassign: PropTypes.bool,
        assigneeUserFullName: PropTypes.string,
        assigneeUserId: PropTypes.number,
        createdDate: PropTypes.number,
        description: PropTypes.string,
        dueDate: PropTypes.instanceOf(Date),
        endDate: PropTypes.instanceOf(Date),
        finished: PropTypes.bool,
        flowFileCaseId: PropTypes.number,
        flowModule: PropTypes.string,
        id: PropTypes.number,
        name: PropTypes.string,
        ordinal: PropTypes.number,
        startDate: PropTypes.instanceOf(Date),
        status: PropTypes.string,
        trnFlowId: PropTypes.number,
        dueAlert: PropTypes.shape({
            dueStatus: PropTypes.string,
            message: PropTypes.string,
        }),
        moduleInfo: PropTypes.shape({
            module: PropTypes.string,
            fileCaseId: PropTypes.number,
            fileNo: PropTypes.string,
        }),
    }).isRequired,
    resources: PropTypes.shape({
        users: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    reloadTasks: PropTypes.func,
};

Task.defaultProps = {
    resources: {
        users: [],
    },
    reloadTasks: () => true,
};

export default Task;
